import { useMemo } from "react";
import { AlertCircle, Lightbulb, Sparkles } from "lucide-react";
import { dictionary, Language } from "../../utils/dictionary";
import { MetaActionPlan } from "../../services/meta/metaService";
import StatusBadge, { SemaforoStatus } from "./StatusBadge";

// Semáforo ranges per metric — mirrors the backend funnel_benchmarks
const METRIC_LABELS: Record<string, string> = {
  hook_rate: "Hook Rate",
  thruplay_rate: "ThruPlay Rate",
  ctr: "CTR",
  cpc: "CPC",
  roas: "ROAS",
  click_to_purchase: "Click → Compra",
};

const METRIC_REFERENCES: Record<string, string> = {
  hook_rate: "≥ 35%",
  thruplay_rate: "≥ 15%",
  ctr: "≥ 1.5%",
  cpc: "≤ $0.50",
  roas: "≥ 3.0x",
  click_to_purchase: "≥ 3%",
};

const SEMAFORO_COLORS: Record<SemaforoStatus, { card: string; text: string; diagnosis: string }> = {
  red: { card: "border-red-200 bg-red-50/30", text: "text-red-700", diagnosis: "bg-red-50 border-red-200" },
  yellow: { card: "border-amber-200 bg-amber-50/30", text: "text-amber-700", diagnosis: "bg-amber-50 border-amber-200" },
  green: { card: "border-emerald-200 bg-emerald-50/30", text: "text-emerald-700", diagnosis: "bg-emerald-50 border-emerald-200" },
};

const DIAGNOSIS_TITLE_KEY: Record<SemaforoStatus, string> = {
  red: "analysis-diagnosis-critical",
  yellow: "analysis-diagnosis-attention",
  green: "analysis-diagnosis-positive",
};

const formatMetricValue = (metric: string, snapshot: Record<string, unknown>): string => {
  const insights = (snapshot?.insights as Record<string, unknown>) ?? {};
  const actions = (insights.actions as Array<Record<string, unknown>>) ?? [];
  const impressions = Number(insights.impressions ?? 0);

  const findAction = (type: string): number => {
    const action = actions.find((a) => a.action_type === type);
    if (!action) return 0;
    const val = action.value;
    return typeof val === "string" ? parseFloat(val) : Number(val ?? 0);
  };

  const sumActionList = (list: Array<Record<string, unknown>> | undefined): number =>
    (list ?? []).reduce((acc, a) => {
      const val = a.value;
      return acc + (typeof val === "string" ? parseFloat(val) : Number(val ?? 0));
    }, 0);

  switch (metric) {
    case "hook_rate": {
      const views = findAction("video_view");
      if (!impressions) return "0%";
      return ((views / impressions) * 100).toFixed(1) + "%";
    }
    case "thruplay_rate": {
      const thruplay = sumActionList(insights.video_thruplay_watched_actions as Array<Record<string, unknown>>);
      if (!impressions) return "0%";
      return ((thruplay / impressions) * 100).toFixed(1) + "%";
    }
    case "ctr":
      return Number(insights.ctr ?? 0).toFixed(2) + "%";
    case "cpc":
      return "$" + Number(insights.cpc ?? 0).toFixed(2);
    case "roas": {
      const roas = insights.purchase_roas as Array<Record<string, unknown>> | undefined;
      const val = roas?.[0]?.value;
      const num = typeof val === "string" ? parseFloat(val) : Number(val ?? 0);
      return num.toFixed(2) + "x";
    }
    case "click_to_purchase": {
      const purchases = findAction("purchase");
      const clicks = findAction("link_click");
      if (!clicks) return "0%";
      return ((purchases / clicks) * 100).toFixed(1) + "%";
    }
    default:
      return "—";
  }
};

interface AnalysisResultProps {
  plan: MetaActionPlan;
  language: Language;
}

export const AnalysisResult = ({ plan, language }: AnalysisResultProps) => {
  const ai = plan.ai_response;
  const worstStatus: SemaforoStatus = useMemo(() => {
    const values = Object.values(ai.semaforo ?? {});
    if (values.includes("red")) return "red";
    if (values.includes("yellow")) return "yellow";
    return "green";
  }, [ai.semaforo]);

  const diagnosisBg = SEMAFORO_COLORS[worstStatus].diagnosis;
  const diagnosisIcon = worstStatus === "green"
    ? <Lightbulb className="w-5 h-5 text-emerald-500" />
    : <AlertCircle className="w-5 h-5 text-red-500" />;
  const diagnosisTitleKey = DIAGNOSIS_TITLE_KEY[worstStatus];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-gray-400 mb-1">
            {new Date(plan.created_at).toLocaleString(language === "es" ? "es-ES" : language === "pt" ? "pt-BR" : "en-US", {
              year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            })}
          </p>
          <h2 className="text-lg font-bold text-gray-800">{ai.analysis_summary.headline}</h2>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={worstStatus} language={language} />
        </div>
      </div>

      {/* Semáforo metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(ai.semaforo).map(([metric, status]) => {
          const colors = SEMAFORO_COLORS[status as SemaforoStatus];
          return (
            <div key={metric} className={`p-3 rounded-lg border ${colors.card}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">{METRIC_LABELS[metric] ?? metric}</span>
                <StatusBadge status={status as SemaforoStatus} language={language} compact />
              </div>
              <p className={`text-xl font-bold ${colors.text}`}>
                {formatMetricValue(metric, plan.metrics_snapshot)}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {dictionary["analysis-reference"]?.[language] ?? "Referencia"}: {METRIC_REFERENCES[metric] ?? "—"}
              </p>
            </div>
          );
        })}
      </div>

      {/* Diagnosis card */}
      <div className={`p-4 rounded-lg border ${diagnosisBg}`}>
        <div className="flex items-start gap-3 mb-2">
          {diagnosisIcon}
          <h3 className="text-sm font-bold text-gray-800">
            {dictionary[diagnosisTitleKey]?.[language] ?? "Diagnóstico"}
          </h3>
        </div>
        <p className="text-sm font-semibold text-gray-800 mb-1">{ai.critical_bottleneck.title}</p>
        <p className="text-sm text-gray-700 mb-2 italic">{ai.critical_bottleneck.financial_impact_text}</p>
        <p className="text-sm text-gray-600">{ai.critical_bottleneck.diagnosis}</p>
      </div>

      {/* Action plan (numbered timeline) */}
      {ai.critical_bottleneck.action_plan.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fluxi" />
            {dictionary["analysis-action-plan"]?.[language] ?? "Plan de acción"}
          </h3>
          <ol className="space-y-2">
            {ai.critical_bottleneck.action_plan.map((step, i) => (
              <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                <span className="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 text-xs font-bold bg-white border border-gray-200 text-gray-600">
                  {i + 1}
                </span>
                <p className="text-sm text-gray-700">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Winning assets */}
      {ai.winning_assets.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-emerald-700 mb-3">
            {dictionary["analysis-winning-assets"]?.[language] ?? "Lo que funciona (¡NO TOCAR!)"}
          </h3>
          <ul className="space-y-2">
            {ai.winning_assets.map((asset, i) => (
              <li key={i} className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-sm font-semibold text-emerald-800">{asset.asset_name}</p>
                <p className="text-sm text-emerald-700 mt-0.5">{asset.message}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Secondary optimizations */}
      {ai.secondary_optimizations.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-amber-700 mb-3">
            {dictionary["analysis-secondary-optimizations"]?.[language] ?? "Optimizaciones secundarias"}
          </h3>
          <ul className="space-y-2">
            {ai.secondary_optimizations.map((opt, i) => (
              <li key={i} className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm font-semibold text-amber-800">{opt.metric}</p>
                <p className="text-sm text-amber-700 mt-0.5">{opt.advice}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

    </div>
  );
};

export default AnalysisResult;
